import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, signal, ViewChild } from '@angular/core';

type Dir = 'left' | 'right' | 'up' | 'down';

const empty = (): number[] => Array(16).fill(0);

function spawn(g: number[]): number[] {
  const cells = g.map((v, i) => (v === 0 ? i : -1)).filter((i) => i >= 0);
  if (!cells.length) return g;
  const k = cells[Math.floor(Math.random() * cells.length)];
  const ng = g.slice();
  ng[k] = Math.random() < 0.9 ? 2 : 4;
  return ng;
}

const init = (): number[] => spawn(spawn(empty()));

function rot(g: number[]): number[] {
  const n = empty();
  for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) n[c * 4 + (3 - r)] = g[r * 4 + c];
  return n;
}

function slideLeft(g: number[]): { out: number[]; gained: number } {
  let gained = 0;
  const out = empty();
  for (let r = 0; r < 4; r++) {
    const row = g.slice(r * 4, r * 4 + 4).filter((v) => v);
    for (let i = 0; i < row.length - 1; i++) {
      if (row[i] === row[i + 1]) {
        row[i] *= 2;
        gained += row[i];
        row.splice(i + 1, 1);
      }
    }
    for (let c = 0; c < 4; c++) out[r * 4 + c] = row[c] || 0;
  }
  return { out, gained };
}

const TILE_COLORS: Record<number, [string, string]> = {
  2: ['#f0e6d8', '#6b5b45'], 4: ['#ece0c8', '#6b5b45'], 8: ['#f5b06a', '#fff'], 16: ['#f39457', '#fff'],
  32: ['#f27a5a', '#fff'], 64: ['#ef5f3c', '#fff'], 128: ['#efc949', '#fff'], 256: ['#efc23c', '#fff'],
  512: ['#efbb2c', '#fff'], 1024: ['#efb31f', '#fff'], 2048: ['#efa50f', '#fff'],
};

/**
 * 2048 — slide numbered tiles with arrow keys or swipe; merge equal tiles to
 * reach 2048. Ported from the OTA-B2B-6 design (games.jsx, TwentyFortyEight).
 * Rendered in the Toolbox Games gallery (floating). Default (Emulated)
 * encapsulation keeps its `tf-*` CSS scoped.
 */
@Component({
  selector: 'app-mini-2048',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './mini-2048.component.html',
  styleUrl: './mini-2048.component.scss',
})
export class Mini2048Component implements AfterViewInit {
  @ViewChild('box') private box?: ElementRef<HTMLElement>;

  readonly grid = signal<number[]>(init());
  readonly score = signal(0);
  readonly over = signal(false);
  readonly won = signal(false);

  private touch: { x: number; y: number } | null = null;

  ngAfterViewInit(): void {
    this.box?.nativeElement.focus();
  }

  tileColor(v: number): [string, string] {
    return TILE_COLORS[v] || ['#3a3f4b', '#fff'];
  }

  tileFontSize(v: number): number {
    return v >= 1024 ? 20 : v >= 128 ? 24 : 28;
  }

  move(dir: Dir): void {
    if (this.over()) return;
    const grid = this.grid();
    let g = grid;
    const times = ({ left: 0, up: 3, right: 2, down: 1 } as Record<Dir, number>)[dir];
    for (let i = 0; i < times; i++) g = rot(g);
    const { out, gained } = slideLeft(g);
    let res = out;
    for (let i = 0; i < (4 - times) % 4; i++) res = rot(res);
    if (res.join(',') === grid.join(',')) return;
    const ng = spawn(res);
    this.grid.set(ng);
    this.score.update((s) => s + gained);
    if (!this.won() && ng.includes(2048)) this.won.set(true);
    const full = !ng.includes(0);
    if (full) {
      let can = false;
      for (let r = 0; r < 4 && !can; r++) for (let c = 0; c < 4 && !can; c++) {
        const v = ng[r * 4 + c];
        if (c < 3 && ng[r * 4 + c + 1] === v) can = true;
        if (r < 3 && ng[(r + 1) * 4 + c] === v) can = true;
      }
      if (!can) this.over.set(true);
    }
  }

  onKey(e: KeyboardEvent): void {
    const m = ({ ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' } as Record<string, Dir>)[e.key];
    if (m) {
      e.preventDefault();
      this.move(m);
    }
  }

  onTouchStart(e: TouchEvent): void {
    const t = e.touches[0];
    this.touch = { x: t.clientX, y: t.clientY };
  }

  onTouchEnd(e: TouchEvent): void {
    if (!this.touch) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - this.touch.x;
    const dy = t.clientY - this.touch.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 24) return;
    this.move(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up'));
  }

  reset(): void {
    this.grid.set(init());
    this.score.set(0);
    this.over.set(false);
    this.won.set(false);
    this.box?.nativeElement.focus();
  }
}
